import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { home_02 } from "@tql/ui-components-react/untitled/icons";
import {
	chevronDown,
	chevronRight,
} from "@tql/ui-components-react/untitled/icons";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router";

export type LeftNavBarItemProps = {
	label: string;
	icon?: typeof home_02;
	customElement?: ReactNode;
	route?: string;
	onClick?: () => void;
	children?: LeftNavBarItemProps[];
	customClass?: string;
	end?: boolean;
};

export const isActive = (
	item: LeftNavBarItemProps,
	currentPathname: string,
): boolean => {
	if (!item.route) {
		return false;
	}
	return (
		currentPathname === item.route ||
		currentPathname.startsWith(`${item.route}/`)
	);
};

export function LeftNavBarItem({
	item,
	level = 0,
	onActivated,
}: Readonly<{
	item: LeftNavBarItemProps;
	level?: number;
	onActivated?: () => void;
}>) {
	const location = useLocation();
	const isItemActive = isActive(item, location.pathname);
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (!item.children || item.children.length === 0) return;
		const anyChildActive = item.children.some((c) =>
			isActive(c, location.pathname),
		);
		setOpen(anyChildActive);
	}, [location.pathname, item.children]);

	const handleClick = (): boolean => {
		if (item.route) {
			return true;
		}

		if (item.onClick) {
			item.onClick();
			return true;
		}

		return false;
	};

	const handleItemClick = (e: React.MouseEvent) => {
		if (item.children && item.children.length > 0 && !item.route) {
			e.stopPropagation();
			setOpen((s) => !s);
			return;
		}

		const activated = handleClick();
		e.stopPropagation();
		if (activated && typeof onActivated === "function") {
			onActivated();
		}
	};

	const handleItemKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" || e.key === " ") {
			if (item.children && item.children.length > 0 && !item.route) {
				e.preventDefault();
				e.stopPropagation();
				setOpen((s) => !s);
				return;
			}

			if (item.route) {
				if (typeof onActivated === "function") {
					onActivated();
				}
				return;
			}

			e.preventDefault();
			const activated = handleClick();
			e.stopPropagation();
			if (activated && typeof onActivated === "function") {
				onActivated();
			}
		}
	};

	const handleChevronClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setOpen((s) => !s);
	};

	let itemIcon: ReactNode = null;

	if (item.customElement) {
		itemIcon = item.customElement;
	} else if (item.icon) {
		itemIcon = <SvgIcon icon={item.icon} size={"xlarge"} />;
	}

	const chevronAriaLabel = open
		? "Collapse " + item.label
		: "Expand " + item.label;
	const childrenList =
		item.children && item.children.length > 0 ? item.children : [];

	// Indentation is applied via padding on the item itself (rather than a
	// margin/padding on a wrapping container) so the active/hover background
	// still spans the full width of the navbar, matching top-level items.
	const indentStyle: React.CSSProperties | undefined =
		level > 0
			? { paddingLeft: `calc(var(--kendo-spacing-6) + ${level * 30}px)` }
			: undefined;

	const chevronStyle: React.CSSProperties = {
		alignItems: "center",
		background: "transparent",
		border: "none",
		display: "flex",
		padding: 4,
		color: "#fff",
	};

	const renderedChildren = childrenList.map((child) => {
		const childKey = child.route ?? child.label;
		return (
			<LeftNavBarItem
				key={childKey}
				item={child}
				level={level + 1}
				onActivated={onActivated}
			/>
		);
	});

	const itemContent = (
		<>
			{itemIcon ? (
				<div className={"left-navbar-item-icon"}>{itemIcon}</div>
			) : null}
			<div className={"left-navbar-item-label"}>{item.label}</div>

			{childrenList.length > 0 && (
				<button
					aria-expanded={open}
					aria-label={chevronAriaLabel}
					className={"left-navbar-item-chevron"}
					onClick={handleChevronClick}
					style={chevronStyle}
					tabIndex={0}
					type={"button"}
				>
					<SvgIcon icon={open ? chevronDown : chevronRight} size={"large"} />
				</button>
			)}
		</>
	);

	return (
		<>
			{item.route ? (
				<NavLink
					className={({ isActive: isNavLinkActive }) =>
						`left-navbar-item${isNavLinkActive ? " active" : ""}${item.customClass ? " " + item.customClass : ""}`
					}
					end={item.end}
					onClick={() => onActivated?.()}
					style={indentStyle}
					to={item.route}
				>
					{itemContent}
				</NavLink>
			) : (
				<div
					className={`left-navbar-item${isItemActive ? " active" : ""}${item.customClass ? " " + item.customClass : ""}`}
					onClick={handleItemClick}
					onKeyDown={handleItemKeyDown}
					role={"button"}
					style={indentStyle}
					tabIndex={0}
				>
					{itemContent}
				</div>
			)}

			{childrenList.length > 0 && open ? (
				<div className={"navbar-item-children-container"}>
					{renderedChildren}
				</div>
			) : null}
		</>
	);
}
